import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, SquareArrowOutUpRight } from 'lucide-react'
import { Modal } from './modal'
import { Spinner } from './spinner'
import { FlowProgress } from './flow-progress'
import { holdingToPicked, TokenPicker, type PickedToken } from './token-picker'
import { useToast } from './toast'
import { errorMessage, useTRPC } from '@/lib/trpc'
import type { Holding, MeWallet, PrivateWithdrawalView, RouterOutputs, VaultWithdrawalView } from '@/lib/api-types'
import { formatAmount, formatSol, formatTokenAmount, formatUsd, LAMPORTS_PER_SOL, plural } from '@/lib/format'
import { noteOwnActivity, useInvalidatePortfolio } from '@/lib/portfolio'
import { useRefreshActivity } from '@/lib/activity-feed'
import { useSetCustody } from '@/lib/custody'
import { PlaceChip, PlaceMenu, platformLabel, usePlatformOptions } from './platform-chip'
import { countdownText, formatEta, useCountdown } from '@/lib/eta'
import { SOL_MINT, USDC_MINT, USDT_MINT } from '@/lib/default-tokens'
import { CUSTODY_LABELS, isExchangeId, usePoolMode, useKrakenAvailable, useCustody, type ExchangeId } from '@/lib/pool'
import type { Chain } from '../../../src/constants/chains'

const SOLANA = 'solana' as Chain
const STABLE_MINTS = new Set([USDC_MINT, USDT_MINT])
type Quote = RouterOutputs['transfer']['quote']
type Request = { toAddress: string; amountRaw: string; tokenAddress?: string }
type Step =
  | { kind: 'form' }
  | { kind: 'review'; quote: Quote; request: Request }
  | { kind: 'reviewPrivate'; toAddress: string; amountRaw: bigint; full: boolean }
  | { kind: 'sending'; transferId: string }
  | { kind: 'done'; transferId: string }
  | { kind: 'private'; id: string }
  | { kind: 'reviewVault'; toAddress: string; amountRaw: bigint }
  | { kind: 'vault'; id: string }
  /** the progress screen from the moment Withdraw is clicked, while the request is created (John, Oct 31 2026); `back` is the review step to return to if it fails */
  | { kind: 'starting'; flow: 'transfer' | 'private' | 'vault'; back: Step }
const PRIVATE_TERMINAL = new Set(['completed', 'failed', 'stalled'])
type Summary = { amount: string; from?: string; toAddress: string; routing: string; place?: string }
/** a withdrawal already on its way, opened from its chip in the in-progress bar (John, Oct 31 2026) */
export type TrackedWithdrawal = { kind: 'vault'; id: string } | { kind: 'private'; id: string } | { kind: 'transfer'; transferId: string }

function toRaw(value: string, decimals: number): bigint | null {
  const match = /^(\d*)(?:\.(\d*))?$/.exec(value.trim())
  if (!match || (match[1] === '' && !match[2])) return null
  const raw = BigInt((match[1] || '0') + (match[2] ?? '').slice(0, decimals).padEnd(decimals, '0'))
  return raw > 0n ? raw : null
}
const isSolanaAddress = (value: string) => /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(value)
const sol = (lamports: bigint | string | null | undefined) =>
  lamports === null || lamports === undefined ? '' : `${formatSol(Number(lamports) / LAMPORTS_PER_SOL)} SOL`
/** raw units of a token with its symbol */
const amountOf = (raw: bigint | string | null | undefined, asset: { symbol: string; decimals: number }) =>
  raw === null || raw === undefined ? '' : `${formatTokenAmount(Number(raw) / 10 ** asset.decimals, asset.symbol, 6)} ${asset.symbol}`
/** one part, plus one more for every threshold the amount reaches */
const partCount = (amountRaw: bigint, thresholdsRaw: string[]) => 1 + thresholdsRaw.filter((t) => amountRaw >= BigInt(t)).length

export function WithdrawModal({
  open,
  onClose,
  wallet,
  holdings,
  initialToken = null,
  track = null,
}: {
  open: boolean
  onClose: () => void
  wallet: MeWallet
  holdings: Holding[]
  /** opened from a holdings row: that token is selected */
  initialToken?: PickedToken | null
  /** opened from the in-progress bar: straight to that withdrawal's progress */
  track?: TrackedWithdrawal | null
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const toast = useToast()
  const invalidatePortfolio = useInvalidatePortfolio()
  const refreshActivity = useRefreshActivity()
  const poolMode = usePoolMode()
  const { custody, exchange: exchangeCustody } = useCustody()
  const [step, setStep] = useState<Step>({ kind: 'form' })
  const [picked, setPicked] = useState<PickedToken | null>(null)
  // a token held in more than one place (John, Nov 2 2026: HELL at Sonar and in the vault): the place it leaves from
  const [tokenFrom, setTokenFrom] = useState<string | null>(null)
  const [amount, setAmount] = useState('')
  /** "Max" was pressed: everything leaves and the fees come out of it */
  const [maxed, setMaxed] = useState(false)
  const [toAddress, setToAddress] = useState('')
  // where the money goes (John, Nov 1 2026: the Kraken button became a cycler): any wallet address typed in, or a
  // fresh deposit address at Kraken or one of the exchanges, filled in
  const [target, setTarget] = useState('wallet')
  const viaTarget = target !== 'wallet'
  const targetLabel = platformLabel(target)
  const [targetMinimum, setTargetMinimum] = useState<number | null>(null)
  const krakenOn = useKrakenAvailable()
  const platformOptions = usePlatformOptions()
  const { set: setCustody } = useSetCustody()
  // the place chosen right now: an address that arrives for a place picked earlier and since changed is dropped, so the
  // To menu never has to be disabled while one is fetched (John, Nov 1 2026)
  const targetRef = useRef(target)
  useEffect(() => {
    targetRef.current = target
  }, [target])
  const addressRequest = useRef<Promise<{ address: string; minimum: number | null } | null> | null>(null)
  const targetAddress = useMutation(
    trpc.vault.withdrawTargetAddress.mutationOptions({
      onSuccess: (result, variables) => {
        if (targetRef.current !== variables.target) return
        setToAddress(result.address)
        setTargetMinimum(result.minimum)
        setError(null)
        if (!result.fresh && variables.target === 'kraken') toast('info', 'Kraken gave no new address', 'An older unused one was filled in.')
      },
      onError: (err, variables) => {
        if (targetRef.current !== variables.target) return
        setTarget('wallet')
        setError(errorMessage(err))
      },
    }),
  )
  // Review pressed while that address is still coming: it waits for it, instead of the button greying out (John, Nov 1 2026)
  const [waiting, setWaiting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // what is being withdrawn and where: stays on screen through every progress state (John, Oct 29 2026)
  const [summary, setSummary] = useState<Summary | null>(null)
  useEffect(() => {
    if (open) {
      setStep(track ? (track.kind === 'vault' ? { kind: 'vault', id: track.id } : track.kind === 'private' ? { kind: 'private', id: track.id } : { kind: 'sending', transferId: track.transferId }) : { kind: 'form' })
      setAmount(''); setMaxed(false); setToAddress(''); setTarget('wallet'); setTargetMinimum(null); setError(null); setPicked(initialToken); setTokenFrom(null); setSummary(null)
      // an address handed out last time may have been used since: read the bank again
      void queryClient.invalidateQueries({ queryKey: trpc.vault.withdrawTargetSpare.queryKey() })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, track])

  const options = useMemo(() => holdings.filter((t) => t.chain === 'solana' && Number(t.balance) > 0), [holdings])
  const selected =
    (picked ? options.find((t) => (picked.isNative ? t.isNative : t.address === picked.address)) : undefined) ?? options.find((t) => t.isNative)
  const decimals = selected?.decimals ?? 9
  // on the exchange side SOL, USDC and USDT are paid by the exchange from what it holds: that is the balance here,
  // not the dashboard's figure, which also counts the fee SOL kept in the vault (Nov 1 2026)
  const exchangeId = isExchangeId(custody) ? custody : null
  const exchangeBalances = useQuery({ ...trpc.vault.exchangeBalances.queryOptions(), enabled: open && exchangeCustody && exchangeId !== null, refetchInterval: 15_000 })
  const exchangeAsset = selected ? (selected.isNative ? 'SOL' : selected.address === USDC_MINT ? 'USDC' : selected.address === USDT_MINT ? 'USDT' : null) : null
  const exchangeHeld = exchangeId && exchangeAsset ? exchangeBalances.data?.[exchangeId]?.[exchangeAsset] : undefined
  // the balance on the platform the money leaves from (John, Nov 1 2026: all balances show together, the split per platform rides on the row)
  // the places that hold a token, for its From menu; SOL, USDC and USDT leave from the remembered platform
  const tokenPlaces = exchangeAsset || !selected ? [] : (selected.platforms?.map((entry) => entry.platform) ?? [selected.custody === 'vault' ? 'vault' : 'wallet'])
  const fromPlatform = exchangeAsset ? custody : tokenFrom && tokenPlaces.includes(tokenFrom) ? tokenFrom : (tokenPlaces[0] ?? 'wallet')
  const platformSlice = selected?.platforms ? (selected.platforms.find((entry) => entry.platform === fromPlatform)?.balance ?? '0') : undefined
  const balanceRaw = exchangeHeld !== undefined ? BigInt(exchangeHeld) : platformSlice !== undefined ? BigInt(platformSlice) : selected ? BigInt(selected.balance) : 0n
  const balance = selected ? Number(balanceRaw) / 10 ** selected.decimals : 0
  const symbol = (selected?.symbol ?? 'SOL').toUpperCase()
  const solBalanceLamports = BigInt(holdings.find((t) => t.chain === 'solana' && t.isNative)?.balance ?? '0')
  const vaultMode = selected?.custody === 'vault'
  // the exchange's flat fee comes on top of the amount: shown before the withdrawal is confirmed (John, Oct 31 2026)
  const payoutFee = useQuery({
    ...trpc.vault.payoutFee.queryOptions({ tokenAddress: selected?.isNative ? SOL_MINT : (selected?.address ?? '') }),
    enabled: exchangeCustody && !!selected,
    staleTime: 5 * 60_000,
  })
  const isSol = selected?.isNative ?? false
  const isStable = !!selected && !selected.isNative && STABLE_MINTS.has(selected.address)
  // Kraken and the exchanges take SOL, USDC and USDT over Solana; nothing else is offered (John, Oct 30 2026)
  const targetSymbol = (['SOL', 'USDC', 'USDT'] as const).find((asset) => asset === (selected?.isNative ? 'SOL' : (selected?.symbol ?? '').toUpperCase())) ?? null
  // the places a SOL, USDC or USDT withdrawal can go with one click: any address, Kraken, the exchanges other than the one it
  // leaves from, and only those that take the asset over Solana (Coinbase has no Solana USDT, Nov 1 2026)
  const takers = useQuery({ ...trpc.vault.withdrawTargets.queryOptions({ symbol: targetSymbol ?? 'SOL' }), enabled: open && targetSymbol !== null, staleTime: 5 * 60_000 })
  const takes = (id: string) => !takers.data || takers.data.includes(id as (typeof takers.data)[number])
  const targetOptions = targetSymbol
    ? [{ id: 'wallet', label: 'External wallet address' }, ...(krakenOn && takes('kraken') ? [{ id: 'kraken', label: 'Kraken' }] : []), ...platformOptions.filter((option) => option.id !== custody && takes(option.id))]
    : [{ id: 'wallet', label: 'External wallet address' }]
  // the bank's ready address per target, read when the dialog opens so a pick fills at once (John, Nov 1 2026)
  const placeTargets = targetOptions.filter((option) => option.id !== 'wallet').map((option) => option.id as 'kraken' | 'binanceus' | 'mexc' | 'coinbase')
  const spares = useQueries({
    queries: placeTargets.map((id) => ({
      ...trpc.vault.withdrawTargetSpare.queryOptions({ target: id, symbol: targetSymbol ?? 'SOL' }),
      enabled: open && targetSymbol !== null,
      staleTime: 10_000,
    })),
  })
  const pickTarget = (id: string) => {
    setError(null)
    setTargetMinimum(null)
    setToAddress('')
    addressRequest.current = null
    if (id === 'wallet' || !targetSymbol) {
      setTarget('wallet')
      return
    }
    setTarget(id)
    const spare = spares[placeTargets.indexOf(id as (typeof placeTargets)[number])]?.data
    if (spare) {
      setToAddress(spare.address)
      setTargetMinimum(spare.minimum)
      return
    }
    addressRequest.current = targetAddress.mutateAsync({ target: id as 'kraken' | 'binanceus' | 'mexc' | 'coinbase', symbol: targetSymbol }).catch(() => null)
  }
  // what each place holds of the token, beside its name in the From list (John, Nov 1 2026)
  const holdingAt = (platform: string) => {
    if (!selected) return undefined
    const raw = selected.platforms ? (selected.platforms.find((entry) => entry.platform === platform)?.balance ?? '0') : selected.balance
    const amount = Number(raw) / 10 ** selected.decimals
    return `${formatTokenAmount(amount, symbol)}${selected.priceUsd ? ` (${formatUsd(amount * selected.priceUsd)})` : ''}`
  }
  const fromOptions = platformOptions.map((option) => ({ ...option, detail: holdingAt(option.id) }))
  // SOL, USDC and USDT always leave privately (John, Oct 29 2026): no toggle
  const privateOn = !vaultMode && (isSol || isStable)
  const privateConfig = useQuery({ ...trpc.privateWithdrawal.config.queryOptions(), enabled: open, staleTime: 60_000 })
  // what the exchange took last time, for the review (John, Nov 1 2026)
  const flowEta = useQuery({
    ...trpc.vault.flowEta.queryOptions({ platform: (isExchangeId(custody) ? custody : 'mexc') as 'binanceus' | 'mexc' | 'coinbase', symbol: targetSymbol ?? 'SOL', to: viaTarget ? (target as 'kraken' | 'binanceus' | 'mexc' | 'coinbase') : undefined }),
    enabled: open && isExchangeId(custody) && targetSymbol !== null,
    staleTime: 30_000,
  })
  const cfg = privateConfig.data
  const assetCfg = cfg ? (isSol ? cfg.sol : cfg.stable) : undefined
  const asset = { symbol, decimals }
  /** the USD figure after an amount of the token on the review rows (John, Nov 1 2026) */
  const withUsd = (amount: number) => (selected?.priceUsd ? ` (${formatUsd(amount * selected.priceUsd)})` : '')
  /** the same for any token the holdings price, by symbol (SOL = the native row): fee rows and progress lines (John, Nov 1 2026) */
  const usdOf = (amount: number, sym: string | null | undefined) => {
    const upper = (sym ?? '').toUpperCase()
    const price = upper ? holdings.find((t) => t.chain === 'solana' && (upper === 'SOL' ? t.isNative : !t.isNative && (t.symbol ?? '').toUpperCase() === upper))?.priceUsd : undefined
    return price ? ` (${formatUsd(amount * price)})` : ''
  }
  const assetUsd = (raw: bigint) => withUsd(Number(raw) / 10 ** decimals)
  const solUsd = (lamports: bigint) => usdOf(Number(lamports) / LAMPORTS_PER_SOL, 'SOL')
  /** an amount of the token with its USD figure */
  const amountWithUsd = (raw: bigint) => `${amountOf(raw, asset)}${assetUsd(raw)}`
  const amountRaw = toRaw(amount, decimals) ?? 0n
  // The fees come on top of the amount (John, Oct 29 2026): Relay's fee per part, the
  // payout's fee for SOL, and one transaction per part from the trading wallet. The
  // part count follows the total, so it is settled together with the fees.
  const payoutFeeLamports = cfg && isSol ? BigInt(cfg.payoutFeeLamports) : 0n
  const plannedParts = (raw: bigint) => {
    if (!assetCfg) return 1
    const perPart = BigInt(assetCfg.relayFeePerPartRaw)
    let parts = partCount(raw, assetCfg.partThresholdsRaw)
    for (let i = 0; i < 4; i += 1) {
      const next = partCount(raw + perPart * BigInt(parts) + payoutFeeLamports, assetCfg.partThresholdsRaw)
      if (next === parts) break
      parts = next
    }
    return parts
  }
  const parts = plannedParts(amountRaw)
  /** the most Relay's fees can come to for an amount, in the asset, so the estimate never promises too little */
  const relayFeeFor = (raw: bigint) => (assetCfg ? BigInt(assetCfg.relayFeePerPartRaw) * BigInt(plannedParts(raw)) : 0n)
  const relayFeeCeiling = relayFeeFor(amountRaw)
  /** network fees in SOL: one hop per part, plus the payout for SOL (a stablecoin's payout is paid by Dispence) */
  const networkFeeFor = (raw: bigint) => (cfg ? BigInt(cfg.txFeeLamports) * BigInt(plannedParts(raw)) + payoutFeeLamports : 0n)
  const networkFeeLamports = networkFeeFor(amountRaw)
  const networkFeeReserve = cfg ? BigInt(cfg.txFeeLamports) * BigInt(cfg.parts.max) : 0n
  /** the largest amount the balance covers once the fees sit on top of it */
  const maxRaw = (() => {
    if (!privateOn || !assetCfg) return balanceRaw
    let max = balanceRaw
    for (let i = 0; i < 4; i += 1) {
      const next = balanceRaw - relayFeeFor(max) - (isSol ? networkFeeFor(max) : 0n)
      if (next === max) break
      max = next
    }
    return max > 0n ? max : 0n
  })()
  // "Max" means the whole balance: truncated (never rounded up) to what the input can hold
  const shownDecimals = Math.min(decimals, 6)
  const maxAmountText = (Number(balanceRaw / 10n ** BigInt(decimals - shownDecimals)) / 10 ** shownDecimals).toString()

  const quote = useMutation(trpc.transfer.quote.mutationOptions({
    onSuccess: (q, variables) => setStep({ kind: 'review', quote: q, request: { toAddress: variables.toAddress, amountRaw: variables.amountRaw, tokenAddress: variables.tokenAddress } }),
    onError: (err) => setError(errorMessage(err)),
  }))
  // a start reads the lists again at once, so the in-progress bar shows it without waiting for a poll (John, Oct 31 2026)
  const create = useMutation(trpc.transfer.create.mutationOptions({
    onSuccess: (res) => { setStep({ kind: 'sending', transferId: res.transferId }); refreshActivity(['transfer']) },
    onError: (err) => setError(errorMessage(err)),
  }))
  const createPrivate = useMutation(trpc.privateWithdrawal.create.mutationOptions({
    onSuccess: (res) => { setStep({ kind: 'private', id: res.id }); refreshActivity(['privateWithdrawal']) },
    onError: (err) => setError(errorMessage(err)),
  }))
  const transferId = step.kind === 'sending' || step.kind === 'done' ? step.transferId : null
  const status = useQuery({
    ...trpc.transfer.status.queryOptions({ transferId: transferId ?? '' }),
    enabled: transferId !== null,
    refetchInterval: (q) => {
      const s = q.state.data?.status
      return s === 'completed' || s === 'errored' || s === 'failed' ? false : 2_000
    },
  })
  useEffect(() => {
    if (step.kind !== 'sending' || !status.data) return
    const data = status.data
    const transferId = step.transferId
    if (data.status === 'completed') {
      setStep({ kind: 'done', transferId })
      noteOwnActivity([data.symbol])
      void invalidatePortfolio()
    } else if (data.status === 'errored' || data.status === 'failed') {
      setError(data.error ?? 'Withdrawal failed.')
      setStep({ kind: 'form' })
      toast('error', 'Withdrawal failed', data.error ?? undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.data?.status])

  const vaultWithdraw = useMutation(trpc.vault.withdraw.mutationOptions({
    onSuccess: (res) => { setStep({ kind: 'vault', id: res.id }); refreshActivity(['withdrawal']) },
    onError: (err) => setError(errorMessage(err)),
  }))
  const vaultId = step.kind === 'vault' ? step.id : null
  const vaultStatus = useQuery({
    ...trpc.vault.withdrawal.queryOptions({ id: vaultId ?? '' }),
    enabled: vaultId !== null,
    // kept up while the far end (an exchange, Kraken) is still crediting: the dialog's reads are what notice the credit
    refetchInterval: (q) => {
      const data = q.state.data
      if (!data) return 3_000
      const crediting = data.transfer !== null && data.transfer.status !== 'credited' && data.transfer.status !== 'failed'
      return PRIVATE_TERMINAL.has(data.status) && !crediting ? false : 3_000
    },
  })
  const vw = vaultStatus.data
  useEffect(() => {
    if (step.kind !== 'vault' || !vw) return
    // a transfer to one of our own exchanges is announced once, when the far end has credited it (John, Nov 1 2026)
    if (vw.status === 'completed' && (!vw.transfer || vw.transfer.status === 'credited')) {
      const symbol = (vw.tokenSymbol ?? '').toUpperCase()
      noteOwnActivity([symbol])
      void invalidatePortfolio()
    } else if (vw.status === 'failed') {
      setError(vw.error ?? 'Withdrawal failed.')
      setStep({ kind: 'form' })
      toast('error', 'Withdrawal failed', vw.error ?? undefined)
    } else if (vw.status === 'stalled') {
      toast('error', 'Withdrawal needs a look', vw.error ?? undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vw?.status])
  const privateId = step.kind === 'private' ? step.id : null
  const privateStatus = useQuery({
    ...trpc.privateWithdrawal.get.queryOptions({ id: privateId ?? '' }),
    enabled: privateId !== null,
    refetchInterval: (q) => (q.state.data && PRIVATE_TERMINAL.has(q.state.data.status) ? false : 2_000),
  })
  const pw = privateStatus.data
  useEffect(() => {
    if (step.kind !== 'private' || !pw) return
    if (pw.status === 'completed') {
      noteOwnActivity([pw.asset.symbol])
      void invalidatePortfolio()
    } else if (pw.status === 'failed') {
      setError(pw.error ?? 'Withdrawal failed.')
      setStep({ kind: 'form' })
      toast('error', 'Withdrawal failed', pw.error ?? undefined)
    } else if (pw.status === 'stalled') {
      toast('error', 'Withdrawal needs a look', pw.error ?? undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pw?.status])

  async function review() {
    setError(null)
    let address = toAddress.trim()
    let minimum = targetMinimum
    if (viaTarget && !address && addressRequest.current) {
      setWaiting(true)
      const result = await addressRequest.current
      setWaiting(false)
      // the fetch failed (its message is up already) or the place was changed meanwhile
      if (!result || targetRef.current !== target) return
      address = result.address
      minimum = result.minimum
    }
    if (!isSolanaAddress(address)) return setError('Enter a valid Solana address.')
    if (address === wallet.solanaAddress) return setError('That is this wallet\'s own address.')
    if (viaTarget && minimum !== null && (Number(amount) || 0) < minimum) return setError(`${targetLabel} takes at least ${minimum} ${targetSymbol}.`)
    const raw = toRaw(amount, decimals)
    if (!raw) return setError('Enter an amount.')
    if (Number(amount) > balance + 1e-12) return setError('Amount is more than your balance.')
    if (vaultMode) {
      setStep({ kind: 'reviewVault', toAddress: address, amountRaw: raw })
      return
    }
    if (privateOn) {
      if (!cfg || !assetCfg) return setError('Still loading the routing limits, try again.')
      if (raw < BigInt(assetCfg.minRaw)) return setError(`The smallest private withdrawal is ${amountOf(assetCfg.minRaw, asset)}.`)
      // "Max": everything leaves and the fees come out of it, so the balance itself is the amount
      const everything = maxed && raw >= maxRaw
      if (!everything && raw > maxRaw) return setError(isSol ? 'Not enough SOL: the fees come on top of the amount. Use Max to withdraw everything.' : `Not enough ${symbol}: the fee comes on top of the amount. Use Max to withdraw everything.`)
      if (everything && raw - relayFeeCeiling - (isSol ? networkFeeLamports : 0n) < BigInt(assetCfg.minRaw)) return setError(`Not enough ${symbol} to withdraw once the fees are paid.`)
      if (!isSol && solBalanceLamports < networkFeeReserve) return setError('Keep about 0.0005 SOL in the wallet for the network fees of the parts.')
      setStep({ kind: 'reviewPrivate', toAddress: address, amountRaw: raw, full: everything })
      return
    }
    quote.mutate({ walletId: wallet.id, chain: SOLANA, toAddress: address, amountRaw: raw.toString(), tokenAddress: selected && !selected.isNative ? selected.address : undefined })
  }

  // reopened on a running withdrawal (the bar's chip, a link), the dialog shows what the confirm screen showed, built from
  // the record (John, Nov 1 2026: "close the window, bring it back up and it loses information")
  const derivedSummary: Summary | null =
    step.kind === 'vault' && vw
      ? (() => {
          const sym = (vw.tokenSymbol ?? '').toUpperCase()
          const ui = Number(vw.amountRaw) / 10 ** vw.tokenDecimals
          return { amount: `${formatTokenAmount(ui, sym, 6)} ${sym}${usdOf(ui, sym)}`, from: vw.platform, toAddress: vw.toAddress, routing: vw.parts.length > 1 ? 'In parts' : 'Direct', place: vw.toPlace ?? vw.transfer?.place ?? undefined }
        })()
      : step.kind === 'private' && pw
        ? { amount: `${amountOf(pw.amountLamports, pw.asset)}${usdOf(Number(pw.amountLamports) / 10 ** pw.asset.decimals, pw.asset.symbol)}`, from: 'sonar', toAddress: pw.toAddress, routing: plural(pw.parts.length, 'part'), place: undefined }
        : (step.kind === 'sending' || step.kind === 'done') && status.data
          ? { amount: `${formatTokenAmount(status.data.amountUi ?? 0, status.data.symbol, 6)} ${status.data.symbol}${usdOf(status.data.amountUi ?? 0, status.data.symbol)}`, toAddress: status.data.toAddress, routing: '1 part', place: undefined }
          : null
  const shownSummary = summary ?? derivedSummary
  const privateDone = step.kind === 'private' && pw !== undefined && PRIVATE_TERMINAL.has(pw.status)
  const vaultDone = step.kind === 'vault' && vw !== undefined && PRIVATE_TERMINAL.has(vw.status) && (!vw.transfer || vw.transfer.status === 'credited' || vw.transfer.status === 'failed')
  const footer =
    step.kind === 'form' ? (
      <button type="button" className="btn btn-primary w-full" disabled={quote.isPending || waiting} onClick={() => void review()}>
        {quote.isPending || waiting ? <Spinner /> : 'Review'}
      </button>
    ) : step.kind === 'review' ? (
      <div className="flex gap-3">
        <button type="button" className="btn btn-secondary flex-1" onClick={() => setStep({ kind: 'form' })}>Back</button>
        <button
          type="button"
          className="btn btn-primary flex-1"
          disabled={create.isPending}
          onClick={() => {
            setSummary({ amount: `${formatTokenAmount(step.quote.amountUi, step.quote.symbol, 6)} ${step.quote.symbol}${withUsd(step.quote.amountUi)}`, toAddress: step.quote.toAddress, routing: '1 part', place: viaTarget ? target : undefined })
            const back = step
            setStep({ kind: 'starting', flow: 'transfer', back })
            create.mutate({ walletId: wallet.id, chain: SOLANA, ...step.request, clientRequestId: crypto.randomUUID() }, { onError: () => setStep(back) })
          }}
        >
          {create.isPending ? <Spinner /> : 'Confirm withdrawal'}
        </button>
      </div>
    ) : step.kind === 'reviewPrivate' ? (
      <div className="flex gap-3">
        <button type="button" className="btn btn-secondary flex-1" onClick={() => setStep({ kind: 'form' })}>Back</button>
        <button
          type="button"
          className="btn btn-primary flex-1"
          disabled={createPrivate.isPending}
          onClick={() => {
            setSummary({ amount: amountWithUsd(step.amountRaw), from: 'sonar', toAddress: step.toAddress, routing: plural(parts, 'part'), place: viaTarget ? target : undefined })
            const back = step
            setStep({ kind: 'starting', flow: 'private', back })
            createPrivate.mutate({ walletId: wallet.id, toAddress: step.toAddress, amountLamports: step.amountRaw.toString(), tokenAddress: isSol ? undefined : selected?.address, fullBalance: step.full || undefined }, { onError: () => setStep(back) })
          }}
        >
          {createPrivate.isPending ? <Spinner /> : 'Withdraw'}
        </button>
      </div>
    ) : step.kind === 'reviewVault' ? (
      <div className="flex gap-3">
        <button type="button" className="btn btn-secondary flex-1" onClick={() => setStep({ kind: 'form' })}>Back</button>
        <button
          type="button"
          className="btn btn-primary flex-1"
          disabled={vaultWithdraw.isPending || !selected}
          onClick={() => {
            if (!selected) return
            setSummary({ amount: `${formatTokenAmount(Number(step.amountRaw) / 10 ** decimals, symbol, 6)} ${symbol}${withUsd(Number(step.amountRaw) / 10 ** decimals)}`, from: fromPlatform, toAddress: step.toAddress, routing: poolMode ? 'Direct' : 'In parts', place: viaTarget ? target : undefined })
            const back = step
            setStep({ kind: 'starting', flow: 'vault', back })
            // the pool keys SOL by the wrapped-SOL mint; the holdings row carries the native sentinel
            vaultWithdraw.mutate({ walletId: wallet.id, tokenAddress: selected.isNative ? SOL_MINT : selected.address, amountRaw: step.amountRaw.toString(), toAddress: step.toAddress, from: !exchangeAsset && fromPlatform === 'vault' ? 'vault' : undefined }, { onError: () => setStep(back) })
          }}
        >
          {vaultWithdraw.isPending ? <Spinner /> : 'Withdraw'}
        </button>
      </div>
    ) : step.kind === 'done' || privateDone || vaultDone ? (
      <div className="flex gap-3">
        <button type="button" className="btn btn-secondary flex-1" onClick={() => { setStep({ kind: 'form' }); setAmount(''); setMaxed(false); setToAddress(''); setTarget('wallet'); setTargetMinimum(null); setError(null); setSummary(null) }}>
          New withdrawal
        </button>
        <button type="button" className="btn btn-primary flex-1" onClick={onClose}>Done</button>
      </div>
    ) : undefined

  return (
    <Modal open={open} onClose={onClose} title="Withdraw" footer={footer}>
      {step.kind === 'form' ? (
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="grid gap-1.5">
              <span className="text-muted">Token</span>
              <div className="flex">
                <TokenPicker value={selected ? holdingToPicked(selected) : null} onChange={(t) => { setPicked(t); setTokenFrom(null); setAmount(''); setMaxed(false); if (viaTarget) { setToAddress(''); setTarget('wallet'); setTargetMinimum(null) } }} holdings={holdings} allowSearch={false} heldOnly showSymbol />
              </div>
            </div>
            <div className="grid gap-1.5">
              <span className="text-muted">From</span>
              {/* where it leaves from (John, Nov 1 2026): SOL, USDC and USDT from any platform, each with what it holds, the pick remembered; a token from the place that holds it */}
              {targetSymbol && platformOptions.length > 0 ? (
                <PlaceMenu value={custody} options={fromOptions} align="right" onChange={(id) => { setAmount(''); setMaxed(false); setCustody.mutate({ custody: id as ExchangeId }) }} />
              ) : selected ? (
                <PlaceMenu value={fromPlatform} options={tokenPlaces.map((id) => ({ id, label: id === 'wallet' ? 'Wallet' : platformLabel(id), detail: holdingAt(id) }))} align="right" onChange={(id) => { setTokenFrom(id); setAmount(''); setMaxed(false) }} />
              ) : null}
            </div>
          </div>
          <label className="grid gap-1.5 text-sm">
            <span className="flex items-center justify-between text-muted">
              <span>Amount</span>
              <button type="button" className="text-xs text-accent" onClick={() => { setAmount(maxAmountText); setMaxed(privateOn) }}>Max {formatAmount(balance)}{withUsd(balance)}</button>
            </span>
            <div className="control flex items-center gap-2 px-3">
              <input className="w-full bg-transparent py-2.5 text-lg outline-none" inputMode="decimal" placeholder="0.00" value={amount} onChange={(e) => { setAmount(e.target.value); setMaxed(false) }} />
              <span className="text-sm text-muted">{symbol}</span>
            </div>
            <span className="text-xs text-muted">{selected?.priceUsd ? formatUsd((Number(amount) || 0) * selected.priceUsd) : ''}</span>
          </label>
          {/* not one wrapping label: a click anywhere in it pressed the Kraken button, the label's first control (John, Oct 31 2026) */}
          <div className="grid gap-1.5 text-sm">
            <span className="flex items-center justify-between text-muted">
              <label htmlFor="withdraw-to">To</label>
              <PlaceMenu value={target} options={targetOptions} onChange={pickTarget} align="right" opens="up" className="max-w-[60%]" />
            </span>
            <div className="control flex items-center px-3">
              <input id="withdraw-to" className="w-full bg-transparent py-2.5 font-mono text-base sm:text-sm outline-none" placeholder={targetAddress.isPending ? 'Getting address…' : 'Solana address'} value={toAddress} readOnly={viaTarget} onChange={(e) => { setToAddress(e.target.value); setTarget('wallet'); setTargetMinimum(null) }} spellCheck={false} />
            </div>
          </div>
          {error ? <p className="text-sm text-negative">{error}</p> : null}
        </div>
      ) : step.kind === 'review' ? (
        <div className="grid gap-3 text-sm">
          <Row label="Withdraw" value={`${formatTokenAmount(step.quote.amountUi, step.quote.symbol, 6)} ${step.quote.symbol}${withUsd(step.quote.amountUi)}`} />
          <Row label="To" value={<code className="break-all font-mono text-xs">{step.quote.toAddress}</code>} /><PlaceRow place={viaTarget ? target : undefined} />
          <Row label="Network fee" value={`${formatTokenAmount(step.quote.feeUi, step.quote.nativeSymbol, 6)} ${step.quote.nativeSymbol}${step.quote.feeUsd ? ` (${formatUsd(step.quote.feeUsd)})` : ''}`} />
          {step.quote.isFullBalance ? <p className="text-xs text-muted">Withdrawing the full balance; the fee is taken from it.</p> : null}
          {step.quote.warnings?.length ? (
            <ul className="grid gap-1 rounded-control bg-surface-2 p-3 text-xs text-warning">
              {step.quote.warnings.map((w) => <li key={w}>{w}</li>)}
            </ul>
          ) : null}
          {error ? <p className="text-sm text-negative">{error}</p> : null}
        </div>
      ) : step.kind === 'reviewVault' ? (
        <div className="grid gap-3 text-sm">
          <Row label="Withdraw" value={`${formatTokenAmount(Number(step.amountRaw) / 10 ** decimals, symbol, 6)} ${symbol}${withUsd(Number(step.amountRaw) / 10 ** decimals)}`} />
          <FromRow from={fromPlatform} /><Row label="To" value={<code className="break-all font-mono text-xs">{step.toAddress}</code>} /><PlaceRow place={viaTarget ? target : undefined} />
          {payoutFee.data ? (
            <Row label={`${payoutFee.data.label} fee`} value={`${formatTokenAmount(Number(payoutFee.data.feeRaw) / 10 ** payoutFee.data.decimals, payoutFee.data.symbol, 6)} ${payoutFee.data.symbol}${usdOf(Number(payoutFee.data.feeRaw) / 10 ** payoutFee.data.decimals, payoutFee.data.symbol)}`} />
          ) : null}
          {flowEta.data && isExchangeId(custody) ? <Row label="Estimated time" value={formatEta(flowEta.data.withdrawSeconds)} /> : null}
          {flowEta.data && isExchangeId(custody) && flowEta.data.arrivalSeconds !== null ? <Row label={`Credited at ${targetLabel}`} value={formatEta(flowEta.data.arrivalSeconds)} /> : null}
          {error ? <p className="text-sm text-negative">{error}</p> : null}
        </div>
      ) : step.kind === 'vault' ? (
        <div className="grid gap-4">
          <SummaryRows summary={shownSummary} />
          <VaultProgress withdrawal={vw} usd={usdOf} />
        </div>
      ) : step.kind === 'reviewPrivate' ? (
        <div className="grid gap-3 text-sm">
          {step.full ? (
            <>
              <Row label="Withdraw" value={`${amountOf(step.amountRaw, asset)}${withUsd(Number(step.amountRaw) / 10 ** decimals)}, everything`} />
              <FromRow from={fromPlatform} /><Row label="To" value={<code className="break-all font-mono text-xs">{step.toAddress}</code>} /><PlaceRow place={viaTarget ? target : undefined} />
              <Row label="Fees" value={`up to ${amountWithUsd(isSol ? relayFeeCeiling + networkFeeLamports : relayFeeCeiling)}${isSol ? '' : ` and ${sol(networkFeeLamports)}${solUsd(networkFeeLamports)}`}, taken out`} />
              <Row label="Recipient receives" value={`at least ${amountWithUsd(step.amountRaw - relayFeeCeiling - (isSol ? networkFeeLamports : 0n))}`} />
            </>
          ) : (
            <>
              <Row label="Recipient receives" value={`${amountOf(step.amountRaw, asset)}${withUsd(Number(step.amountRaw) / 10 ** decimals)}`} />
              <FromRow from={fromPlatform} /><Row label="To" value={<code className="break-all font-mono text-xs">{step.toAddress}</code>} /><PlaceRow place={viaTarget ? target : undefined} />
              <Row label="Fees" value={`up to ${amountWithUsd(isSol ? relayFeeCeiling + networkFeeLamports : relayFeeCeiling)}${isSol ? '' : ` and ${sol(networkFeeLamports)}${solUsd(networkFeeLamports)}`}, on top`} />
              <Row label="You pay" value={`up to ${amountWithUsd(step.amountRaw + relayFeeCeiling + (isSol ? networkFeeLamports : 0n))}${isSol ? '' : ` and ${sol(networkFeeLamports)}${solUsd(networkFeeLamports)}`}`} />
            </>
          )}
          {error ? <p className="text-sm text-negative">{error}</p> : null}
        </div>
      ) : step.kind === 'sending' || (step.kind === 'starting' && step.flow === 'transfer') ? (
        <div className="grid gap-4">
          <SummaryRows summary={shownSummary} />
          <p className="text-sm">{status.data?.status === 'transactionSent' ? 'Transaction sent, waiting for confirmation…' : 'Building and signing the transaction…'}</p>
          <Spinner className="mx-auto" />
        </div>
      ) : step.kind === 'starting' ? (
        <div className="grid gap-4">
          <SummaryRows summary={shownSummary} />
          {step.flow === 'vault' ? <VaultProgress withdrawal={undefined} usd={usdOf} /> : <PrivateProgress withdrawal={undefined} usd={usdOf} />}
        </div>
      ) : step.kind === 'private' ? (
        <div className="grid gap-4">
          <SummaryRows summary={shownSummary} />
          <PrivateProgress withdrawal={pw} usd={usdOf} />
        </div>
      ) : (
        <div className="grid gap-4">
          <SummaryRows summary={shownSummary} />
          <div className="flex items-center gap-2 text-sm"><CheckCircle2 className="size-5 shrink-0 text-accent" /> Done. {formatTokenAmount(status.data?.amountUi ?? 0, status.data?.symbol, 6)} {status.data?.symbol}{usdOf(status.data?.amountUi ?? 0, status.data?.symbol)} sent.</div>
          {status.data?.explorerUrl ? (
            <a className="btn btn-secondary" href={status.data.explorerUrl} target="_blank" rel="noreferrer">
              View transaction <SquareArrowOutUpRight className="size-4" />
            </a>
          ) : null}
        </div>
      )}
    </Modal>
  )
}

const PRIVATE_STEPS = ['Sending', 'Routing', 'Paying', 'Complete'] as const

function privateStepIndex(status: PrivateWithdrawalView['status']) {
  switch (status) {
    case 'sending':
      return 0
    case 'relayPending':
      return 1
    case 'paying':
      return 2
    case 'completed':
      return 3
    default:
      return -1
  }
}

/** usd: " ($x)" after an amount of the token named, when its price is known */
function PrivateProgress({ withdrawal, usd }: { withdrawal: PrivateWithdrawalView | undefined; usd: (amount: number, symbol: string | null | undefined) => string }) {
  // nothing read yet: the bar is already on screen at its first step (John, Oct 31 2026)
  if (!withdrawal) {
    return <FlowProgress labels={[...PRIVATE_STEPS]} step={0} done={false} failed={false} status="Sending…" />
  }
  const parts = withdrawal.parts.length
  const sent = withdrawal.parts.filter((p) => p.sent).length
  const paid = withdrawal.parts.filter((p) => p.paid).length
  const step = privateStepIndex(withdrawal.status)
  const done = withdrawal.status === 'completed'
  const failed = withdrawal.status === 'failed'
  const status = done
    ? `Done. The recipient received ${amountOf(withdrawal.paidLamports, withdrawal.asset)}${usd(Number(withdrawal.paidLamports ?? 0) / 10 ** withdrawal.asset.decimals, withdrawal.asset.symbol)}.`
    : withdrawal.status === 'sending'
      ? `Sending${parts > 1 ? ` ${plural(parts, 'part')}` : ''}…`
      : withdrawal.status === 'relayPending'
        ? `Routing${parts > 1 ? `, ${paid} of ${parts} parts` : ''}…`
        : withdrawal.status === 'paying'
          ? 'Paying the recipient…'
          : withdrawal.status === 'stalled'
            ? 'Taking longer than usual. Nothing is lost; we are looking into it.'
            : (withdrawal.error ?? 'Withdrawal failed.')
  return (
    <FlowProgress
      flowKey={withdrawal.id}
      labels={PRIVATE_STEPS.map((label) =>
        label === 'Sending' && parts > 1 ? `Sending ${Math.min(sent, parts)}/${parts}` : label === 'Routing' && parts > 1 ? `Routing ${paid}/${parts}` : label,
      )}
      step={withdrawal.status === 'stalled' ? 1 : step}
      done={done}
      failed={failed}
      status={status}
      links={done && withdrawal.explorerUrl ? [{ label: 'View txn', href: withdrawal.explorerUrl }] : []}
      fees={done ? withdrawal.fees : undefined}
    />
  )
}

function VaultProgress({ withdrawal, usd }: { withdrawal: VaultWithdrawalView | undefined; usd: (amount: number, symbol: string | null | undefined) => string }) {
  // a move to one of our own exchanges is one flow (John, Nov 1 2026): it ends when the far end has credited it
  const transfer = withdrawal?.transfer ?? null
  const moving = withdrawal?.status === 'completed' && transfer !== null && transfer.status !== 'credited' && transfer.status !== 'failed'
  const place = transfer ? platformLabel(transfer.place) : ''
  // the exchange's usual payout time counting down under the line (John, Nov 1 2026: "~10s remaining"), then the far end's crediting time
  const countdown = useCountdown(withdrawal?.startedAt, withdrawal?.etaSeconds, withdrawal !== undefined && withdrawal.status !== 'completed' && withdrawal.status !== 'failed')
  const crediting = useCountdown(transfer?.seenAt, transfer?.etaSeconds, moving)
  const left = countdownText(countdown)
  const timing = left ? ` ${left}` : ''
  const creditLeft = countdownText(crediting)
  if (!withdrawal) {
    return <FlowProgress labels={['Sending', 'Paying', 'Complete']} step={0} done={false} failed={false} status="Sending…" />
  }
  const parts = withdrawal.parts.length
  const paid = withdrawal.parts.filter((p) => p.paid).length
  const amount = `${formatTokenAmount(Number(withdrawal.amountRaw) / 10 ** withdrawal.tokenDecimals, withdrawal.tokenSymbol, 6)} ${withdrawal.tokenSymbol ?? ''}${usd(Number(withdrawal.amountRaw) / 10 ** withdrawal.tokenDecimals, withdrawal.tokenSymbol)}`
  const done = withdrawal.status === 'completed' && !moving && transfer?.status !== 'failed'
  const failed = withdrawal.status === 'failed' || transfer?.status === 'failed'
  const step = withdrawal.status === 'scheduled' || withdrawal.status === 'sending' ? 0 : withdrawal.status === 'settling' || withdrawal.status === 'stalled' ? 1 : 2
  const status = done
    ? `Done. ${amount} ${transfer ? `credited at ${place}` : 'sent'}.`
    : moving
      ? `${place} is crediting it…${creditLeft ? ` ${creditLeft}` : ''}`
      : transfer?.status === 'failed'
        ? `${place} did not credit it. Nothing is lost; we are looking into it.`
    : withdrawal.status === 'scheduled' || withdrawal.status === 'sending'
      ? `${withdrawal.note ?? `Sending ${amount}${parts > 1 ? ` in ${parts} parts` : ''}…`}${timing}`
      : withdrawal.status === 'settling'
        ? `${withdrawal.note ?? `Sending, ${paid} of ${parts} parts…`}${timing}`
        : withdrawal.status === 'stalled'
          ? 'Taking longer than usual. Nothing is lost; we are looking into it.'
          : (withdrawal.error ?? 'Withdrawal failed.')
  const firstTx = withdrawal.parts.find((p) => p.transactionHash)?.transactionHash
  return (
    <FlowProgress
      flowKey={withdrawal.id}
      labels={transfer ? ['Sending', 'Paying', 'Crediting'] : ['Sending', 'Paying', 'Complete']}
      step={step}
      done={done}
      failed={failed}
      status={status}
      links={firstTx ? [{ label: 'View txn', href: `https://solscan.io/tx/${firstTx}` }] : []}
      fees={done ? withdrawal.fees : undefined}
    />
  )
}

/** The amount and destination, kept in view while the withdrawal runs and once it is done. */
function SummaryRows({ summary }: { summary: Summary | null }) {
  if (!summary) return null
  return (
    <div className="grid gap-2 rounded-control bg-surface-2 p-3 text-sm">
      <Row label="Withdraw" value={summary.amount} />
      <FromRow from={summary.from} />
      <Row label="To" value={<code className="break-all font-mono text-xs">{summary.toAddress}</code>} />
      <PlaceRow place={summary.place} />
      <Row label="Routing" value={summary.routing} />
    </div>
  )
}

/** where the money leaves from, the row above To (John, Nov 1 2026) */
function FromRow({ from }: { from?: string }) {
  return from ? <Row label="From" value={<PlaceChip id={from} />} /> : null
}
/** the place behind the address when it is one we know (Kraken, one of our exchanges), on its own row under To rather than beside the address (John, Nov 1 2026) */
function PlaceRow({ place }: { place?: string }) {
  return place ? <Row label="Destination" value={<PlaceChip id={place} />} /> : null
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="shrink-0 text-muted">{label}</span>
      {/* an address wraps onto the next line on a phone instead of running off the right (John, Nov 1 2026) */}
      <span className="min-w-0 text-right">{value}</span>
    </div>
  )
}
